// `prose`, in the native register — the agent's reply, unlabeled, as markdown.
//
// This slot used to BE the markdown component: the native skin answered
// "prose" with `std-markdown`, the prosemirror-backed reader that also serves
// the docs. That made the skin — and every consumer of the thread — a hard
// importer of prosemirror. Now the slot is a host for the prose seam
// (`render/prose.ts`): whatever renderer the page installed paints into it,
// and a page that installed nothing gets the library's plain markdown. The
// skin stays a stylesheet with a name; the mechanics live behind the seam.
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Renderers } from "../render/prose";

@component({ name: "std-turn-prose" })
export class TurnProse extends LonaWebComponent {
  private $doc: HTMLElement = this.$("doc");

  static makeWith(props: TurnProse.Props): TurnProse {
    return (TurnProse.make() as TurnProse).bind(props);
  }

  /** Idempotent: the renderer REPLACES the document rather than appending
   * to it, so a pooled row arriving from another turn renders only the
   * text it was just given. Not awaited — the transcript measures row
   * height with a ResizeObserver, so a row that fills in a later frame
   * re-reports itself instead of leaving a hole. */
  bind(props: TurnProse.Props): this {
    void Renderers.prose().render(this.$doc, props.text, {
      style: TurnProse.$document,
      key: "std-turn-prose",
    });
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        width: 100%;
        min-width: 0;
      }
    `,
  ];

  /** The reply's typography. Applied to the plain renderer's light DOM by
   * this component's own sheet, and handed to a shadow-rooted renderer
   * through the seam so both paint the same rhythm. */
  static $document = css`
    .prose > :first-child { margin-top: 0; }
    .prose > :last-child { margin-bottom: 0; }
    .prose p { --font-size: var(--chat-prose-size, var(--text-11px)); --line-height-ratio: 1.5; --margin-top: 0px; --margin-bottom: 12px; }
    .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 { margin: 16px 0 6px; font-weight: 600; line-height: 1.35; }
    .prose pre { padding: 8px 10px; border-radius: 6px; background: var(--surface-sunken, rgba(127, 127, 127, 0.08)); overflow-x: auto; margin: 8px 0; }
    .prose pre code { white-space: pre; font-family: var(--chat-terminal-font, ui-monospace, Menlo, monospace); font-size: var(--chat-code-size, 0.9em); }
    .prose code { font-family: var(--chat-terminal-font, ui-monospace, Menlo, monospace); font-size: var(--chat-code-size, 0.9em); background: var(--surface-sunken, rgba(127, 127, 127, 0.1)); padding: 1px 4px; border-radius: 3px; }
    .prose blockquote { margin: 8px 0; padding: 0 0 0 10px; border-left: 3px solid var(--divider, #ccc); color: var(--text-sec); }
    .prose ul, .prose ol { padding-left: 22px; margin: 6px 0; }
    .prose table { border-collapse: collapse; margin: 8px 0; }
    .prose th, .prose td { border: 1px solid var(--divider, rgba(127, 127, 127, 0.3)); padding: 3px 8px; text-align: left; }
    .prose a { color: var(--chat-link-ink, var(--link, var(--blue))); text-decoration: underline; text-underline-offset: 2px; }
  `;

  static $html: Option<HTMLTemplateElement> = html`<div id="doc"></div>`;
}

export namespace TurnProse {
  export type Props = { text: string };
}
